import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "appThemeMode";
const DEFAULT_VERSION_KEY = "appThemeDefault";
const DEFAULT_MODE = "dark";

const applyDocumentTheme = (mode) => {
  const root = document.documentElement;
  if (mode === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  root.style.colorScheme = mode === "dark" ? "dark" : "light";
};

const ThemeModeContext = createContext(null);

export const ThemeModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const usingDarkDefault = localStorage.getItem(DEFAULT_VERSION_KEY) === DEFAULT_MODE;
    const next = usingDarkDefault && (saved === "light" || saved === "dark") ? saved : DEFAULT_MODE;
    if (!usingDarkDefault) localStorage.setItem(DEFAULT_VERSION_KEY, DEFAULT_MODE);
    applyDocumentTheme(next);
    return next;
  });

  useEffect(() => {
    applyDocumentTheme(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const setMode = useCallback((nextMode) => {
    if (nextMode !== "light" && nextMode !== "dark") return;
    setModeState(nextMode);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      mode,
      isDark: mode === "dark",
      setMode,
      toggleMode,
    }),
    [mode, setMode, toggleMode]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
};
